import { flip, offset, shift } from '@floating-ui/react-dom';
import { RefreshCcw } from '@styled-icons/feather';
import { MediaEmbedElement } from '@typedream/core';
import { toDOMNode } from '@udecode/plate-core';
import classNames from 'classnames';
import { memo, useEffect, useState } from 'react';
import { Floating } from '../../../components/Floating';
import { SelectMedia } from '../../../components/SelectMedia';
import { TOOLS_INTEGRATIONS_DOCUMENTATION_LINK } from '../../../constants';
import { OverlayPortal } from '../../../overlay/OverlayPortal';
import { useTPlateEditorRef } from '../../../plate';
import { useSelectedElementEntry } from '../../../plugins/elementSelection';
import { changeEmbed } from '../../../nodes/mediaEmbed/transforms';
import styles from '../SideToolbar.module.css';

function EmbedSource() {
  const editor = useTPlateEditorRef();
  const selectedElementEntry = useSelectedElementEntry<MediaEmbedElement>();
  const [isReplacingEmbed, setIsReplacingEmbed] = useState(false);

  useEffect(() => setIsReplacingEmbed(false), [selectedElementEntry]);

  useEffect(() => {
    const handler = () => setIsReplacingEmbed((v) => !v);
    editor.emitter.on('toolbar:toggle-replace-embed', handler);
    return () => editor.emitter.off('toolbar:toggle-replace-embed', handler);
  }, [editor.emitter]);

  if (!selectedElementEntry) return null;
  const [element, path] = selectedElementEntry;
  const domNode = toDOMNode(editor, element);

  const onSelect = (type: 'link' | 'code' | 'upload', value: string) => {
    const typesMap = {
      link: 'url',
      code: 'html',
      upload: 'file',
    } as const;

    changeEmbed(editor, typesMap[type], value, { at: path });
  };

  return (
    <div className={styles.section}>
      <span className={styles.label}>Media Embed</span>
      <button
        type="button"
        className={classNames(styles.button)}
        onClick={() => setIsReplacingEmbed((v) => !v)}
      >
        <RefreshCcw size={16} className="mr-2" />
        <span>Replace Embed</span>
      </button>

      {isReplacingEmbed && domNode && (
        <OverlayPortal>
          <Floating
            reference={domNode}
            options={{
              placement: 'bottom',
              middleware: [
                offset(-6),
                flip(),
                shift({
                  crossAxis: true,
                  padding: { top: 8, bottom: 8 },
                }),
              ],
            }}
            autoUpdate
            className={styles.box}
          >
            <SelectMedia
              tabs={{
                link: {
                  button: 'Embed link & iframe',
                  placeholder: 'https://... or <iframe...',
                  defaultValue: element.url,
                  onSelect: (url) => onSelect('link', url),
                  description:
                  'Works with links from YouTube, Vimeo, Google Maps, CodePen',
                },
                upload: {
                  accept: 'audio/*',
                  button: 'Upload file',
                  description: 'Supported: audio files (mp3, mp4, etc)',
                  onSelect: async (file) => {
                    if (!editor.fileManager) return;

                    try {
                      const url = await editor.fileManager.upload(file);
                      onSelect('upload', url);
                    } catch (e) {
                      // do nothing
                    }
                  },
                },
                code: {
                  button: 'Insert Code',
                  placeholder: '<div>Enter your embed code</div>...',
                  defaultValue: element.html,
                  onSelect: (code) => onSelect('code', code),
                  description: (
                    <>
                      <span>Enter your HTML Embed Code.</span>
                      <br />
                      <a href={TOOLS_INTEGRATIONS_DOCUMENTATION_LINK} target="_new">
                        Recommended Integrations
                        {' ->'}
                      </a>
                    </>
                  ),
                },
              }}
            />
          </Floating>
        </OverlayPortal>
      )}
    </div>
  );
}

export default memo(EmbedSource);
